import { SubcategoryAllocation } from '../types/zoneMapType';
import { subcategoryColorByIndex } from '../zoneViewer/zoneMapColorHelper';

export interface StoreSubcategoryInput {
  subcategoryId?: string | null;
  subcategoryName?: string | null;
  capacityPercentage?: number | null;
  zoneFixtureIds?: string[] | null;
}

/**
 * Builds an ordered array of SubcategoryAllocation from macroplanogram storeSubcategories,
 * sorted by first fixture appearance in the pick-path sequence.
 *
 * Colors are assigned by sorted index using golden-angle HSL spacing so adjacent
 * subcategories in the pick path get visually distinct colors.
 *
 * @param storeSubcategories - Raw subcategory data from macroplanogram
 * @param fixtureOrderingMap - Map of fixtureId → sequence order from saved pick path
 * @returns Ordered SubcategoryAllocation[] with colors assigned by fixture-sequence position
 */
export function buildOrderedAllocations(
  storeSubcategories: StoreSubcategoryInput[],
  fixtureOrderingMap?: Map<string, number>,
): SubcategoryAllocation[] {
  // Deduplicate and filter to valid subcategories with positive percentages
  const subcatData: { id: string; name: string; percentage: number }[] = [];
  const seenIds = new Set<string>();

  for (const sc of storeSubcategories) {
    const pct = sc.capacityPercentage;
    if (pct == null || pct <= 0) continue;
    const subId = sc.subcategoryId;
    if (!subId || seenIds.has(subId)) continue;
    seenIds.add(subId);
    const percentage = pct <= 1 ? Math.round(pct * 100) : Math.round(pct);
    if (percentage <= 0) continue;
    subcatData.push({ id: subId, name: sc.subcategoryName ?? subId, percentage });
  }

  // Sort by first fixture appearance in the pick-path sequence
  if (fixtureOrderingMap && fixtureOrderingMap.size > 0) {
    const firstAppearance = new Map<string, number>();
    for (const sc of storeSubcategories) {
      if (!sc.subcategoryId || !seenIds.has(sc.subcategoryId)) continue;
      let minSeq = Infinity;
      for (const fId of sc.zoneFixtureIds ?? []) {
        const seq = fixtureOrderingMap.get(fId);
        if (seq != null && seq < minSeq) minSeq = seq;
      }
      if (minSeq < Infinity) firstAppearance.set(sc.subcategoryId, minSeq);
    }
    subcatData.sort((a, b) => {
      const seqA = firstAppearance.get(a.id) ?? Infinity;
      const seqB = firstAppearance.get(b.id) ?? Infinity;
      return seqA - seqB;
    });
  }

  return subcatData.map((sc, idx) => ({
    subcategoryId: sc.id,
    subcategoryName: sc.name,
    percentage: sc.percentage,
    color: subcategoryColorByIndex(idx),
  }));
}
